import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const COLLAPSED_KEY = 'gs-widget-collapsed';

const readFlag = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const writeFlag = (key: string, value: boolean): void => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // ignore storage failures (private mode, quota)
  }
};

type WidgetContextValue = {
  isClosed: boolean;
  isCollapsed: boolean;
  open: () => void;
  close: () => void;
  toggleCollapsed: () => void;
};

const GettingStartedWidgetContext = createContext<WidgetContextValue | null>(null);

export const GettingStartedWidgetProvider = ({ children }: { children: ReactNode }) => {
  // `isClosed` is intentionally in-memory only: closing hides the widget for the
  // current session, but a refresh (F5) brings it back. Collapse stays persisted.
  const [isClosed, setIsClosed] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => readFlag(COLLAPSED_KEY));

  const open = useCallback(() => {
    setIsClosed(false);
    setIsCollapsed(false);
    writeFlag(COLLAPSED_KEY, false);
  }, []);

  const close = useCallback(() => {
    setIsClosed(true);
  }, []);

  const toggleCollapsed = useCallback(
    () =>
      setIsCollapsed((prev) => {
        const next = !prev;
        writeFlag(COLLAPSED_KEY, next);
        return next;
      }),
    [],
  );

  const value = useMemo(
    () => ({ isClosed, isCollapsed, open, close, toggleCollapsed }),
    [isClosed, isCollapsed, open, close, toggleCollapsed],
  );

  return <GettingStartedWidgetContext.Provider value={value}>{children}</GettingStartedWidgetContext.Provider>;
};

export const useGettingStartedWidget = (): WidgetContextValue => {
  const ctx = useContext(GettingStartedWidgetContext);
  if (!ctx) {
    throw new Error('useGettingStartedWidget must be used within GettingStartedWidgetProvider');
  }
  return ctx;
};
